import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import QurealSection from "../common/QurealSection";
import SectionHeader from "../components/commons/SectionHeader";
import { EditOutlined } from "@ant-design/icons";
import { Button, message, Modal, Spin } from "antd";
import {
    SaveOutlined,
    SendOutlined,
    EyeOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import QurealRenderer from "../qureal_renderer/QurealRenderer";
import BlogEditor from "./BlogEditor";
import {
    useSingleItem,
    useUpdateItemMutation,
    useDeleteItemMutation,
} from "../_actions/app/items";
import { history } from "../_helpers/history";
import styles from "./styles/QurealBlog.module.css";

export default function QurealEditBlog() {
    const { id } = useParams();
    const [blogData, setBlogData] = useState({
        title: "",
        body: "",
        fullContent: "",
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isPreviewVisible, setIsPreviewVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [editorInstance, setEditorInstance] = useState(null);

    // Fetch the blog data
    const {
        data: blog,
        isLoading,
        error: fetchError,
    } = useSingleItem("qblog_posts", `/${id}?fields=*.*`, id !== null);

    // Update mutation
    const updateMutation = useUpdateItemMutation();

    // Delete mutation
    const deleteMutation = useDeleteItemMutation();

    // Set blog data when it's loaded
    useEffect(() => {
        if (blog) {
            setBlogData({
                title: blog.title || "",
                body: blog.body || "",
                fullContent:
                    blog.fullContent ||
                    `<h1>${blog.title || ""}</h1><div>${blog.body || ""}</div>`,
            });
        }
    }, [blog]);
    console.log(blogData);
    const handleEditorChange = (data) => {
        // If data is a string, it's the HTML content
        if (typeof data === "string") {
            setBlogData((prev) => ({
                ...prev,
                body: data,
                fullContent: data,
            }));
        } else {
            // If it's an object with separate fields
            setBlogData((prev) => ({
                ...prev,
                ...data,
            }));
        }
    };
    const handleShowPreview = () => {
        if (!editorInstance) {
            message.error("Make some changes to preview your blog");
            return;
        }

        const currentContent = editorInstance.getCurrentContent();
        console.log("Preview content:", currentContent);

        setBlogData((prev) => ({
            ...prev,
            title: currentContent.title || prev.title,
            body: currentContent.body,
            fullContent: currentContent.fullContent,
        }));

        setIsPreviewVisible(true);
    };
    const handleSubmit = async (status = "draft") => {
        setIsSaving(true);
        try {
            // Validate form
            if (!blogData.title.trim()) {
                message.error("Please enter a title");
                setIsSaving(false);
                return;
            }
            const finalContent = editorInstance
                ? editorInstance.getHTML()
                : blogData.fullContent;
            const blogPayload = {
                title: blogData.title,
                body: blogData.body,
                fullContent: finalContent,
                status: status,
                published_date:
                    status === "published"
                        ? new Date().toISOString()
                        : blog?.published_date || null,
                date_updated: new Date().toISOString(),
            };
            // const blogPayload = {
            //   title: blogData.title,
            //   body: blogData.body,
            //   fullContent: blogData.fullContent,
            //   status: blogData.status,
            //   published_date:
            //     status === "published"
            //       ? new Date().toISOString()
            //       : blog?.published_date || null,
            //   date_updated: new Date().toISOString(),
            // };

            // Update the blog
            updateMutation.mutate(
                {
                    collection: "qblog_posts",
                    id: id,
                    data: blogPayload,
                },
                {
                    onSuccess: () => {
                        message.success(
                            status === "published"
                                ? "Blog published successfully!"
                                : "Draft saved successfully!"
                        );
                        history.push("/setting/preset-manager");
                    },
                    onError: (error) => {
                        console.error("Error updating blog:", error);
                        message.error("Failed to update blog");
                    },
                    onSettled: () => {
                        setIsSaving(false);
                    },
                }
            );
        } catch (error) {
            console.error("Error updating blog:", error);
            message.error("Failed to update blog");
            setIsSaving(false);
        }
    };

    const handleDelete = () => {
        deleteMutation.mutate(
            { collection: "qblog_posts", id: id }, // Replace null with proper params
            {
                onSuccess: () => {
                    message.success("Blog deleted successfully!");
                    history.push("/ecommerce/blog");
                },
                onError: (error) => {
                    console.error("Error deleting blog:", error);
                    message.error("Failed to delete blog");
                },
            }
        );
        setIsDeleteModalVisible(false);
    };

    if (isLoading) {
        return (
            <QurealSection
                header={
                    <SectionHeader
                        heading="Edit Blog"
                        icon={<EditOutlined />}
                    />
                }
            >
                <div className={styles.spinnerContainer}>
                    <Spin size="large" />
                </div>
            </QurealSection>
        );
    }

    if (fetchError) {
        return (
            <QurealSection
                header={
                    <SectionHeader
                        heading="Edit Blog"
                        icon={<EditOutlined />}
                    />
                }
            >
                <div className={styles.errorContainer}>
                    <p>Error loading blog: {fetchError.message}</p>
                    <Button onClick={() => history.push("/ecommerce/blog")}>
                        Back to Blogs
                    </Button>
                </div>
            </QurealSection>
        );
    }

    return (
        <QurealSection
            header={
                <SectionHeader heading="Edit Blog" icon={<EditOutlined />}>
                    <div className={styles.actionButtons}>
                        <Button
                            icon={<EyeOutlined />}
                            onClick={handleShowPreview}
                        >
                            Preview
                        </Button>
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => setIsDeleteModalVisible(true)}
                        >
                            Delete
                        </Button>
                        <Button
                            type="default"
                            icon={<SaveOutlined />}
                            onClick={() => handleSubmit("draft")}
                            loading={isSaving}
                        >
                            Save Draft
                        </Button>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={() => handleSubmit("published")}
                            loading={isSaving}
                        >
                            Publish
                        </Button>
                    </div>
                </SectionHeader>
            }
        >
            {blogData.fullContent && (
                <div className={styles.editorContainer}>
                    <BlogEditor
                        initialContent={blogData.fullContent}
                        onChange={handleEditorChange}
                        onEditorInit={setEditorInstance}
                    />
                </div>
            )}

            {/* Preview Modal */}
            <Modal
                open={isPreviewVisible}
                onCancel={() => setIsPreviewVisible(false)}
                footer={null}
                width={800}
            >
                <div className={styles.rendererWrapper}>
                    <QurealRenderer content={blogData.fullContent} />
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                title="Delete Blog"
                open={isDeleteModalVisible}
                onCancel={() => setIsDeleteModalVisible(false)}
                onOk={handleDelete}
                okText="Delete"
                okButtonProps={{ danger: true }}
                confirmLoading={deleteMutation.isLoading}
            >
                <p>
                    Are you sure you want to delete this blog? This action
                    cannot be undone.
                </p>
            </Modal>
        </QurealSection>
    );
}
